// ─── Prévisualisation des templates générés par l'agent (story 6.3) ──────────
// Helpers purs : extraction des résultats de tools depuis les messages du chat,
// diff entre deux générations, compteur et snap du Split View.

import type { FormTemplate, TemplateField, TemplateSection } from '@/lib/api'
import { isToolResultPayload, type AgentChatMessage } from '@/lib/chatTypes'

// Tools dont le résultat est un template affichable (même forme de `data`).
// Miroir de `TEMPLATE_RESULT_TOOL_NAMES` côté backend (story 6.4).
export const TEMPLATE_RESULT_TOOLS: readonly string[] = ['generateTemplate', 'reviseTemplate']

export type GeneratedTemplate = {
  templateId: string
  name: string
  description: string | null
  sections: TemplateSection[]
}

export type TemplateSnapshot = {
  invocationId: string
  messageId: string
  template: GeneratedTemplate
}

export type TemplateDiff = {
  added: Set<string>
  modified: Set<string>
  removedCount: number
  changedSectionIds: Set<string>
}

const FIELD_TYPES: readonly TemplateField['type'][] = ['text', 'number', 'select', 'file', 'date']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseField(raw: unknown): TemplateField | null {
  if (!isRecord(raw)) return null
  if (typeof raw.id !== 'string' || typeof raw.label !== 'string') return null
  const type = FIELD_TYPES.includes(raw.type as TemplateField['type'])
    ? (raw.type as TemplateField['type'])
    : 'text'
  const field: TemplateField = {
    id: raw.id,
    label: raw.label,
    type,
    required: raw.required === true,
  }
  if (typeof raw.unit === 'string') field.unit = raw.unit
  if (typeof raw.description === 'string') field.description = raw.description
  if (Array.isArray(raw.options) && raw.options.every((o) => typeof o === 'string')) {
    field.options = raw.options as string[]
  }
  return field
}

function parseSection(raw: unknown, index: number): TemplateSection | null {
  if (!isRecord(raw)) return null
  if (typeof raw.id !== 'string' || typeof raw.title !== 'string') return null
  const fields = Array.isArray(raw.fields)
    ? raw.fields.map(parseField).filter((f): f is TemplateField => f !== null)
    : []
  return {
    id: raw.id,
    title: raw.title,
    order: typeof raw.order === 'number' && Number.isFinite(raw.order) ? raw.order : index,
    fields,
  }
}

// Ne lève jamais : renvoie null si le résultat n'est pas un template exploitable.
export function parseTemplateToolResult(result: unknown): GeneratedTemplate | null {
  if (!isToolResultPayload(result) || result.success !== true) return null
  const data = result.data
  if (!isRecord(data)) return null
  if (typeof data.templateId !== 'string' || typeof data.name !== 'string') return null
  if (!Array.isArray(data.sections)) return null
  const sections = data.sections
    .map((s, i) => parseSection(s, i))
    .filter((s): s is TemplateSection => s !== null)
    .sort((a, b) => a.order - b.order)
  return {
    templateId: data.templateId,
    name: data.name,
    description: typeof data.description === 'string' && data.description !== '' ? data.description : null,
    sections,
  }
}

// Story 6.4 — ligne `GET /api/templates/:id` → modèle de preview (même parsing
// défensif que les résultats de tools). `null` si le JSON est inexploitable.
export function generatedTemplateFromFormTemplate(t: FormTemplate): GeneratedTemplate | null {
  if (!Array.isArray(t.sections)) return null
  const sections = t.sections
    .map((s, i) => parseSection(s, i))
    .filter((s): s is TemplateSection => s !== null)
    .sort((a, b) => a.order - b.order)
  return {
    templateId: t.id,
    name: t.name,
    description: typeof t.description === 'string' && t.description !== '' ? t.description : null,
    sections,
  }
}

// Version enregistrée depuis l'éditeur manuel, rattachée au snapshot affiché
// au moment de l'enregistrement (en mémoire uniquement).
export type ManualOverride = {
  baseInvocationId: string
  template: GeneratedTemplate
}

// Story 6.4 — lignée : la base du diff du snapshot N est (a) la version
// manuelle enregistrée sur le snapshot N-1 s'il y en a une, sinon (b) le
// snapshot N-1, UNIQUEMENT s'il porte le même templateId ; sinon null (pas de ✨).
export function selectDiffBase(
  snapshots: readonly TemplateSnapshot[],
  index: number,
  overrides: Readonly<Record<string, ManualOverride>> = {}
): GeneratedTemplate | null {
  const current = snapshots[index]
  const prev = snapshots[index - 1]
  if (!current || !prev) return null
  if (prev.template.templateId !== current.template.templateId) return null
  const override = overrides[current.template.templateId]
  if (override && override.baseInvocationId === prev.invocationId) return override.template
  return prev.template
}

// Snapshots chronologiques des templates générés avec succès dans la conversation.
export function selectTemplateSnapshots(messages: AgentChatMessage[]): TemplateSnapshot[] {
  const snapshots: TemplateSnapshot[] = []
  for (const message of messages) {
    for (const inv of message.toolInvocations ?? []) {
      if (!TEMPLATE_RESULT_TOOLS.includes(inv.name) || inv.status !== 'success') continue
      const template = parseTemplateToolResult(inv.result)
      if (template) snapshots.push({ invocationId: inv.id, messageId: message.id, template })
    }
  }
  return snapshots
}

function norm(value: string | undefined): string {
  return value ?? ''
}

function sameOptions(a: string[] | undefined, b: string[] | undefined): boolean {
  const x = a ?? []
  const y = b ?? []
  return x.length === y.length && x.every((v, i) => v === y[i])
}

function indexFields(t: GeneratedTemplate): Map<string, { field: TemplateField; sectionId: string }> {
  const map = new Map<string, { field: TemplateField; sectionId: string }>()
  for (const section of t.sections) {
    for (const field of section.fields) map.set(field.id, { field, sectionId: section.id })
  }
  return map
}

// Diff par id de champ (unique dans un template, cf. superRefine 6.1).
export function diffTemplates(prev: GeneratedTemplate | null, next: GeneratedTemplate): TemplateDiff {
  const diff: TemplateDiff = {
    added: new Set(),
    modified: new Set(),
    removedCount: 0,
    changedSectionIds: new Set(),
  }
  if (!prev) return diff
  const before = indexFields(prev)
  const after = indexFields(next)
  for (const [id, { field, sectionId }] of after) {
    const old = before.get(id)
    if (!old) {
      diff.added.add(id)
      diff.changedSectionIds.add(sectionId)
      continue
    }
    const o = old.field
    const changed =
      old.sectionId !== sectionId ||
      o.label !== field.label ||
      o.type !== field.type ||
      norm(o.unit) !== norm(field.unit) ||
      norm(o.description) !== norm(field.description) ||
      o.required !== field.required ||
      !sameOptions(o.options, field.options)
    if (changed) {
      diff.modified.add(id)
      diff.changedSectionIds.add(sectionId)
    }
  }
  for (const id of before.keys()) {
    if (!after.has(id)) diff.removedCount += 1
  }
  return diff
}

export function isDiffEmpty(diff: TemplateDiff | null): boolean {
  return !diff || (diff.added.size === 0 && diff.modified.size === 0 && diff.removedCount === 0)
}

function plural(n: number, singular: string, pluralForm: string): string {
  return `${n} ${n > 1 ? pluralForm : singular}`
}

export function formatDiffSummary(diff: TemplateDiff): string {
  const parts: string[] = []
  if (diff.added.size > 0) parts.push(plural(diff.added.size, 'ajouté', 'ajoutés'))
  if (diff.modified.size > 0) parts.push(plural(diff.modified.size, 'modifié', 'modifiés'))
  if (diff.removedCount > 0) parts.push(plural(diff.removedCount, 'supprimé', 'supprimés'))
  return parts.join(', ')
}

export function countTemplate(t: GeneratedTemplate): { sections: number; fields: number } {
  return {
    sections: t.sections.length,
    fields: t.sections.reduce((acc, s) => acc + s.fields.length, 0),
  }
}

export function formatTemplateCount({ sections, fields }: { sections: number; fields: number }): string {
  return `${plural(sections, 'section', 'sections')}, ${plural(fields, 'champ', 'champs')}`
}

// Ratios (en % pour le chat) sur lesquels le Split View s'aimante en fin de drag.
export const SPLIT_SNAP_POINTS: readonly number[] = [0, 40, 50, 60, 100]

export function snapLayout(layout: number[]): number[] {
  const chat = layout[0] ?? 50
  let best = SPLIT_SNAP_POINTS[0]
  for (const p of SPLIT_SNAP_POINTS) {
    const d = Math.abs(p - chat)
    const bd = Math.abs(best - chat)
    // Égalité : on préfère le point non replié (20 → 40, 80 → 60).
    if (d < bd || (d === bd && (best === 0 || best === 100))) best = p
  }
  return [best, 100 - best]
}
